import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Dialog,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { renderAsync } from "docx-preview";
import CloseIcon from "@mui/icons-material/Close";
import { DialogContent, IconButton } from "@mui/material";
import {
  buildProposalService,
  saveProposalService,
} from "../../Services/ProposalBuilderService";

const apiKeyMap = {
  coverPage: "CoverPage",
  toc: "TableOfContent",
  commonInfo: "CommonInformation",
  namedInsured: "NameInsuredOrSchedule",
  premiumSummary: "PremiumSummary",
  coverageSummary: "CoverageSummary",
  schedules: "Schedule",
};

const ProposalSection = ({ proposalData = [], sectionStatus = [], onBack }) => {
  const [builtBlob, setBuiltBlob] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isBackLocked, setIsBackLocked] = useState(false);
  const [buildSuccess, setBuildSuccess] = useState(false);
  const [isProposalBuilt, setIsProposalBuilt] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [error, setError] = useState("");
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [proposalFileName, setProposalFileName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const hasAnySelection = Object.keys(sectionStatus).length > 0;

  const location = useLocation();
  const created_By = location.state?.CreatedBy;
  const lobSplitJobIds = location.state?.lobSplitJobIds || [];
  const lobSplitFlag = lobSplitJobIds?.length > 0;
  const lobs = location.state?.lobEditData?.LobsPreview || [];
  const lobEditData = location.state?.lobEditData || {};
  const targetLobs = [
    "commercial package",
    "homeowners",
    "business owners policy",
    "package",
  ];

  const isTableLoading = isSaving || isBuilding;
  let url = new URL(window.location.href);
  let urlParamToken = url?.searchParams.get("token");
  const docxContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasAnySelection) {
      setError("");
    }
  }, [hasAnySelection]);

  const isSelectable = (value) => {
    return value !== "-" && value !== null && value !== undefined;
  };

  const isChecked = (value) => {
    return value === true || value === "true";
  };

  const [sectionState, setSectionState] = useState(() => {
    const state = {};
    sectionStatus?.forEach((row) => {
      state[row.Lob] = { ...row };
    });
    return state;
  });

  const totalSelectedCount = useMemo(() => {
    let count = 0;
    Object.values(sectionState).forEach((row) => {
      Object.values(apiKeyMap).forEach((key) => {
        if (row[key] === true || row[key] === "true") {
          count++;
        }
      });
    });
    return count;
  }, [sectionState]);

  const handleClearAll = () => {
    setSectionState((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((lob) => {
        Object.values(apiKeyMap).forEach((key) => {
          if (newState[lob][key] !== "-" && newState[lob][key] !== null) {
            newState[lob][key] = false;
          }
        });
      });
      return newState;
    });
  };

  const handleSelectAllRow = (lob) => {
    const row = sectionState[lob];
    const keys = Object.values(apiKeyMap).filter((key) =>
      isSelectable(row[key]),
    );

    const allChecked =
      keys.length > 0 && keys.every((key) => isChecked(row[key]));

    setSectionState((prev) => ({
      ...prev,
      [lob]: {
        ...prev[lob],
        ...keys.reduce((acc, key) => {
          acc[key] = !allChecked;
          return acc;
        }, {}),
      },
    }));
  };

  const columns = [
    // { label: "#", key: "serialnumber" },
    { label: "Line of Business", key: "lob" },
    { label: "Cover Page", key: "coverPage" },
    { label: "Table of Contents", key: "toc" },
    { label: "Common Information", key: "commonInfo" },
    { label: "Named Insured / Name Schedule", key: "namedInsured" },
    { label: "Premium Summary", key: "premiumSummary" },
    { label: "Coverage Summary", key: "coverageSummary" },
    { label: "Schedules", key: "schedules" },
  ];

  const tableData = useMemo(() => {
    if (!Array.isArray(proposalData)) return [];
    return [...new Set(proposalData?.map((p) => p.lobName))].map((lob, i) => ({
      id: i + 1,
      lob,
    }));
  }, [proposalData]);

  const filteredData = useMemo(() => {
    return tableData.filter((row) => {
      const matchesSearch = row.lob
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const rowState = sectionState[row.lob] || {};
      const hasSelection = Object.values(apiKeyMap).some(
        (key) => rowState[key] === true || rowState[key] === "true",
      );
      const matchesFilter =
        filterMode === "all" || (filterMode === "selected" && hasSelection);

      return matchesSearch && matchesFilter;
    });
  }, [tableData, searchTerm, filterMode, sectionState]);

  const payloadObj = (createdBy) => {
    const firstProposal = proposalData?.[0]
    const first = sectionStatus[0];

    const cleanFileName = (file) => {
      // Remove carrier part → everything before "] "
      return file?.startsWith("[")
        ? file.substring(file?.indexOf("]") + 1).trim()
        : file;
    };

    let jobSelections = [];

    if (lobSplitFlag) {
     const filteredLobs = lobEditData?.LobsPreview?.filter((lob) => {
        if (lob?.LobSplit) {
          return !targetLobs?.includes(
            lob?.name?.toLowerCase()
          );
        }

        return true;
      });

      jobSelections = filteredLobs.map((lob) => {
        const proposalMatch = proposalData?.find(
          (p) =>
            p?.JobId === lob?.JobId
        );

        return {
          JobId: lob?.JobId,
          LobId: lob?.LobId,
          LobSplit: lob?.LobSplit == null ? false : lob?.LobSplit,
          Files:
            proposalMatch?.files?.map(cleanFileName) || [],
        };
      });
    } else {
      jobSelections = proposalData
        ?.filter((p) => p.files?.length > 0)
        ?.map((p) => ({
          JobId: p?.JobId,
          LobId: p?.LobId,
          Files: p?.files.map(cleanFileName),
        }));
    }

    // const jobSelections = filterJobGroups
    //   ?.filter((p) => p.files?.length > 0)
    //   ?.map((p) => ({
    //     JobId: p?.JobId,
    //     LobId: p?.LobId,
    //     Files: p?.files.map(cleanFileName),
    //   }));

    return {
      BrokerId: first?.BrokerId,
      ClientName: first?.ClientName,
      CreatedBy: createdBy,
      IsDefaultTemplate: true,
      IsSecondaryTemplate: null,
      JobSelections: jobSelections,
    };
  };

  const handleBuildProposal = async () => {
    try {
      setIsBuilding(true);

      const createdBy = created_By;
      const payload = payloadObj(createdBy);

      const res = await buildProposalService(payload);

      const blob = new Blob([res?.data], {
        type: res.headers["content-type"],
      });

      const proposalFileNameFromHeader = res?.headers["proposal-filename"];
      setProposalFileName(proposalFileNameFromHeader);
      setBuiltBlob(blob);
      setBuildSuccess(true);
      setIsProposalBuilt(true);

      return { blob, fileName: proposalFileNameFromHeader };
    } catch (err) {
      console.error("Build Proposal failed", err);
    } finally {
      setIsBuilding(false);
    }
  };

  const handlePreviewAndDownload = () => {
    if (!builtBlob) return;

    setIsPreviewOpen(true);

    setTimeout(() => {
      if (!docxContainerRef.current) return;

      docxContainerRef.current.innerHTML = "";

      builtBlob.arrayBuffer().then((buffer) => {
        renderAsync(buffer, docxContainerRef.current, null, {
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          breakPages: true,
        });
      });
    }, 0);
  };

  const renderCell = (lob, colKey) => {
    const apiKey = apiKeyMap[colKey];
    const row = sectionState[lob];
    const value = row?.[apiKey];

    if (!isSelectable(value)) {
      return "—";
    }

    return (
      <Tooltip title={`Toggle ${colKey}`} arrow>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border:
              value === true || value === "true"
                ? "0.1px solid #afc0e3"
                : "1px solid #e2e8f0",
            borderRadius: "6px",
            p: 0.3,
            width: "fit-content",
            mx: "auto",
            backgroundColor:
              value === true || value === "true" ? "#eff6ff" : "#f9fafb",
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: "#f0f4ff",
              borderColor: "#93c5fd",
            },
          }}
        >
          <Checkbox
            checked={isChecked(value)}
            size="small"
            onChange={(e) => {
              setSectionState((prev) => ({
                ...prev,
                [lob]: {
                  ...prev[lob],
                  [apiKey]: e.target.checked,
                },
              }));
            }}
            sx={{
              padding: "0px",
              color: "#93c5fd",
              "& .MuiSvgIcon-root": { fontSize: "15px" },
              "&.Mui-checked": { color: "#2563eb" },
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  const handleGenerateProposal = async () => {
    try {
      setIsSaving(true);
      // setIsBuilding(true);

      //  Save
      await saveProposalSectionStatus();

      //  Build
      const result = await handleBuildProposal();
      if (!result) return;

      const { blob, fileName } = result;

      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);
      setIsDownloaded(true);

      navigate(`/createproposalbuilder?token=${urlParamToken}`);

      // Open Preview
      // setIsPreviewOpen(true);

      // setTimeout(() => {
      //   if (!docxContainerRef.current) return;

      //   docxContainerRef.current.innerHTML = "";

      //   blob.arrayBuffer().then(buffer => {
      //     renderAsync(buffer, docxContainerRef.current, null, {
      //       inWrapper: true,
      //       ignoreWidth: false,
      //       ignoreHeight: false,
      //       breakPages: true
      //     });
      //   });
      // }, 0);
    } catch (error) {
      console.error("Generate Proposal failed", error);
    } finally {
      setIsSaving(false);
      setIsBuilding(false);
    }
  };

  const handleSaveAndBuild = async () => {
    try {
      // First Save
      await saveProposalSectionStatus();

      // Then Build
      await handleBuildProposal();
    } catch (error) {
      console.error("Save & Build failed", error);
    }
  };

  const saveProposalSectionStatus = async () => {
    setIsSaving(true);
    setIsBackLocked(true);

    const sectionStatusForSave = lobSplitFlag
      ? lobs?.map((lob) => ({
          JobId: lob?.JobId,
          Lob: lob?.name,
          LobSplit: lob?.LobSplit || false,
        }))
      : sectionStatus;

    try {
      const res = await saveProposalService(
        sectionState,
        sectionStatusForSave,
        lobSplitJobIds,
      );

      if (res?.data?.success) {
        setSaveSuccess(true);
      }
    } catch (error) {
      console.error("Save failed", error);
      setIsBackLocked(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: "5px",
        border: "1px solid #e5e7eb",
        boxShadow: "none",
        margin: "15px 0px 0px 0px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: -1,
        }}
      >
        <Typography sx={{ color: "#6b7280", fontSize: "11px" }}>
          By default, all sections are selected. Uncheck any sections you do not
          want to include.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search Lob..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: 200,
              "& .MuiInputBase-root": {
                fontSize: "12px",
                height: "32px",
                bgcolor: "white",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "18px", color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              sx={{
                height: "32px",
                fontSize: "12px",
                bgcolor: "white",
                "& .MuiSelect-select": { py: 0 }
              }}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon sx={{ fontSize: "16px", color: "#9ca3af", ml: 1 }} />
                </InputAdornment>
              }
            >
              <MenuItem value="all" sx={{ fontSize: "12px" }}>All LOBs</MenuItem>
              <MenuItem value="selected" sx={{ fontSize: "12px" }}>Selected (LOBs) Only</MenuItem>
            </Select>
          </FormControl> */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              bgcolor: totalSelectedCount > 0 ? "#eff6ff" : "#f3f4f6",
              borderRadius: "20px",
              border: "1px solid",
              borderColor: totalSelectedCount > 0 ? "#bfdbfe" : "#e5e7eb",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: totalSelectedCount > 0 ? "#2563eb" : "#9ca3af",
              }}
            >
              Selected Sections: {totalSelectedCount}
            </Typography>
          </Box>

          <Button
            variant="text"
            size="small"
            onClick={handleClearAll}
            disabled={totalSelectedCount === 0}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              color: "#ef4444",
              "&:hover": { bgcolor: "#fef2f2" },
              "&.Mui-disabled": { color: "#d1d5db" },
            }}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {/* TABLE */}
      <Box
        sx={{
          position: "relative",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
        }}
      >
        {isTableLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            <CircularProgress size={36} />
            <Typography sx={{ mt: 1, fontSize: "12px", color: "#374151" }}>
              {isSaving
                ? "Saving proposal configuration..."
                : "Building proposal..."}
            </Typography>
          </Box>
        )}

        <TableContainer sx={{ maxHeight: "220px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.key === "lob" ? "left" : "center"}
                    sx={{
                      fontWeight: 700,
                      fontSize: ".875rem",
                      backgroundColor: "#1e293b",
                      color: "#ffffff",
                      whiteSpace: "nowrap",
                      verticalAlign: "middle",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData?.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      verticalAlign: "middle",
                      width: 200,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {(() => {
                        const rowState = sectionState[row.lob];
                        const keys = Object.values(apiKeyMap).filter((key) =>
                          isSelectable(rowState[key]),
                        );

                        const allChecked =
                          keys.length > 0 &&
                          keys.every((key) => isChecked(rowState[key]));

                        const someChecked = keys.some((key) =>
                          isChecked(rowState[key]),
                        );

                        return (
                          <Tooltip
                            title="Select all sections for this row"
                            arrow
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border:
                                  allChecked || someChecked
                                    ? "0.1px solid #afc0e3"
                                    : "1px solid #e2e8f0",
                                borderRadius: "6px",
                                p: 0.5,
                                backgroundColor:
                                  allChecked || someChecked
                                    ? "#eff6ff"
                                    : "#f9fafb",
                                transition: "all 0.15s ease",
                                "&:hover": {
                                  backgroundColor: "#f0f4ff",
                                  borderColor: "#93c5fd",
                                },
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={allChecked}
                                indeterminate={someChecked && !allChecked}
                                onChange={() => handleSelectAllRow(row.lob)}
                                sx={{
                                  padding: "0px",
                                  color: "#93c5fd",
                                  "& .MuiSvgIcon-root": { fontSize: "15px" },
                                  "&.Mui-checked": { color: "#2563eb" },
                                  "&.MuiCheckbox-indeterminate": {
                                    color: "#2563eb",
                                  },
                                }}
                              />
                            </Box>
                          </Tooltip>
                        );
                      })()}
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "10px",
                          color: "#111827",
                          wordBreak: "break-word",
                        }}
                      >
                        {row.lob}
                      </Typography>
                    </Box>
                  </TableCell>

                  {columns.slice(1).map((col) => (
                    <TableCell key={col.key} align="center">
                      {renderCell(row.lob, col.key)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Snackbar
        open={buildSuccess}
        autoHideDuration={4000}
        onClose={() => setBuildSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setBuildSuccess(false)}
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: "rgb(22 163 74)",
            color: "#fff",
            fontWeight: 500,
            borderRadius: "8px",
          }}
        >
          Proposal configuration saved & built successfully.
        </Alert>
      </Snackbar>

      <Box sx={{ display: "flex", mt: 4, gap: 1 }}>
        <Button
          variant="outlined"
          disabled={isTableLoading}
          // onClick={() => {
          //   if (saveSuccess || isBackLocked) return;
          //   onBack();
          // }}
          onClick={onBack}
          sx={{
            textTransform: "none",
            pl: "22px",
            pr: "24px",
            pt: 0,
            pb: 0,
            borderRadius: "5px",
            height: "35px",
            fontSize: "11.5px",
            fontWeight: 500,
            border: "1px solid #c3c4c580",
            backgroundColor: "white",
            color: "black",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "white",
            },
            "&.Mui-disabled": {
              backgroundColor: "#e5e7eb",
              color: "#9ca3af",
            },
          }}
        >
          Back
        </Button>
        {/* <Button
          variant="contained"
          disabled={
            isTableLoading ||
            isProposalBuilt ||
            totalSelectedCount === 0
          }
          sx={{
            textTransform: "none",
            pl: "22px",
            pr: "24px",
            pt: 0,
            pb: 0,
            borderRadius: "5px",
            height: "35px",
            fontSize: "11.5px",
            fontWeight: 500,
            backgroundColor: isProposalBuilt
              ? "rgb(22 163 74)"
              : "#2563eb",
            color: "white",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: isProposalBuilt
                ? "rgb(22 163 74)"
                : "#2563eb",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgb(229 231 235)",
              color: "rgb(118 124 138)",
              opacity: 1,
            },
          }}
          onClick={handleSaveAndBuild}
        >
          {isProposalBuilt ? "Built ✓" : "Save selection & Build"}
        </Button>

        <Button
          disabled={!builtBlob}
          onClick={handlePreviewAndDownload}
          variant="contained"
          sx={{
            textTransform: "none",
            pl: "22px",
            pr: "24px",
            pt: 0,
            pb: 0,
            borderRadius: "5px",
            height: "35px",
            fontSize: "11.5px",
            fontWeight: 500,
            backgroundColor: "#2563eb",
            color: "white",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#2563eb",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgb(229 231 235)",
              color: "rgb(118 124 138)",
              opacity: 1.5,
            },
          }}
        >
          Preview & Download
        </Button> */}

        <Button
          variant="contained"
          disabled={isTableLoading || totalSelectedCount === 0}
          onClick={handleGenerateProposal}
          sx={{
            textTransform: "none",
            pl: "22px",
            pr: "24px",
            pt: 0,
            pb: 0,
            borderRadius: "5px",
            height: "35px",
            fontSize: "11.5px",
            fontWeight: 500,
            backgroundColor: "#2563eb",
            color: "white",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#2563eb",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgb(229 231 235)",
              color: "rgb(118 124 138)",
            },
          }}
        >
          {isSaving || isBuilding ? "Generating..." : "Generate Proposal"}
        </Button>
      </Box>

      <Dialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        sx={{
          "& .MuiDialog-paper": {
            width: "900px",
            maxWidth: "900px",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="h6">Proposal Preview</Typography>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Download in Word" arrow>
              <Button
                onClick={() => {
                  const url = URL.createObjectURL(builtBlob);

                  const a = document.createElement("a");
                  a.href = url;
                  a.download = proposalFileName;
                  a.click();

                  URL.revokeObjectURL(url);
                  setIsDownloaded(true);
                }}
                startIcon={
                  <img
                    src="/wordimage.png"
                    alt="Word"
                    style={{
                      width: 22,
                      height: 22,
                    }}
                  />
                }
                sx={{
                  background: "linear-gradient(135deg, #38529d, #38529d)",
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "6px",
                  // px: 3,
                  // py: 1,
                  pl: "5px",
                  pr: "9px",
                  pt: "3px",
                  pb: "2px",
                  fontWeight: 600,
                  fontSize: "8px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    // background: "linear-gradient(135deg, #38529d, #38529d)",
                    // boxShadow: "0 6px 16px rgba(28,109,208,0.4)",
                    // transform: "translateY(-2px)",
                  },
                  "& .MuiButton-startIcon": {
                    marginRight: "2px", // reduce from default 8px
                    marginLeft: "0px",
                  },
                }}
              >
                Word
              </Button>
            </Tooltip>

            <IconButton
              onClick={() => {
                setIsPreviewOpen(false);

                if (isDownloaded) {
                  navigate(`/createproposalbuilder?token=${urlParamToken}`);
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent
          dividers
          sx={{
            p: 0,
            height: "75vh",
            overflowY: "auto",
            overflowX: "hidden",
            background: "#f3f4f6",
            // width: "80%"
          }}
        >
          <Box
            ref={docxContainerRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              py: 3,

              "& section": {
                width: "670px !important",
                minHeight: "850px",
                background: "#fff",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                padding: "25px",
              },

              "& table": {
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: "16px",
              },

              "& table, & th, & td": {
                border: "1px solid #000",
              },

              "& th, & td": {
                padding: "6px 8px",
                fontSize: "14px",
              },
            }}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProposalSection;
